import { useRolodexEdit } from 'features/fde'
import debounce from 'lodash/debounce'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import {
  KVAnnotation,
  KVRooftopAnnotation,
  RolodexEntryPatchJson,
  RooftopDamageId,
} from 'types'

const DEBOUNCE_DELAY_MS = 500

interface UseAnnotationAutoSaveProps {
  kestrelModelId: string | undefined
  annotations: KVAnnotation[] | undefined
  setAnnotations: (annotations: KVAnnotation[]) => void
}

interface UseAnnotationAutoSaveReturn {
  updateLabel: (annotationId: string, label: string) => void
  updateSeverity: (annotationId: string, severity: string) => void
  updateNotes: (annotationId: string, notes: string) => void
  updateDamageTypes: (
    annotationId: string,
    damageTypes: RooftopDamageId[]
  ) => void
  flushPendingSave: () => void
}

/**
 * Hook for auto-saving annotation data with debouncing.
 * Updates are saved to the backend after a delay to avoid excessive API calls.
 */
export const useAnnotationAutoSave = ({
  kestrelModelId,
  annotations,
  setAnnotations,
}: UseAnnotationAutoSaveProps): UseAnnotationAutoSaveReturn => {
  const { rolodexPatch } = useRolodexEdit()

  const annotationsRef = useRef(annotations)
  annotationsRef.current = annotations

  const saveToBackend = useCallback(
    (updatedAnnotations: KVAnnotation[]) => {
      if (!kestrelModelId) return

      const patchData: RolodexEntryPatchJson = {
        id: kestrelModelId,
        data: {
          annotations: updatedAnnotations.map((a) => KVAnnotation.toJSON(a)),
        },
      }
      rolodexPatch([patchData])
    },
    [kestrelModelId, rolodexPatch]
  )

  const debouncedSave = useMemo(
    () =>
      debounce((updatedAnnotations: KVAnnotation[]) => {
        saveToBackend(updatedAnnotations)
      }, DEBOUNCE_DELAY_MS),
    [saveToBackend]
  )

  useEffect(() => {
    return () => {
      debouncedSave.cancel()
    }
  }, [debouncedSave])

  const updateLabel = useCallback(
    (annotationId: string, label: string) => {
      const currentAnnotations = annotationsRef.current
      if (!currentAnnotations) return

      const updatedAnnotations = currentAnnotations.map((a) =>
        a.id === annotationId ? { ...a, label } : a
      )

      setAnnotations(updatedAnnotations)
      debouncedSave(updatedAnnotations)
    },
    [setAnnotations, debouncedSave]
  )

  const updateSeverity = useCallback(
    (annotationId: string, severity: string) => {
      const currentAnnotations = annotationsRef.current
      if (!currentAnnotations) return

      const updatedAnnotations = currentAnnotations.map((a) =>
        a.id === annotationId ? { ...a, severity } : a
      )

      setAnnotations(updatedAnnotations)
      debouncedSave(updatedAnnotations)
    },
    [setAnnotations, debouncedSave]
  )

  const updateNotes = useCallback(
    (annotationId: string, notes: string) => {
      const currentAnnotations = annotationsRef.current
      if (!currentAnnotations) return

      const updatedAnnotations = currentAnnotations.map((a) =>
        a.id === annotationId ? { ...a, notes } : a
      )

      setAnnotations(updatedAnnotations)
      debouncedSave(updatedAnnotations)
    },
    [setAnnotations, debouncedSave]
  )

  const updateDamageTypes = useCallback(
    (annotationId: string, damageTypes: RooftopDamageId[]) => {
      const currentAnnotations = annotationsRef.current
      if (!currentAnnotations) return

      const updatedAnnotations = currentAnnotations.map((a) => {
        if (a.id !== annotationId) return a

        const existingRooftop = a.rooftopAnnotation || new KVRooftopAnnotation()
        return {
          ...a,
          rooftopAnnotation: {
            ...existingRooftop,
            selected: damageTypes,
          },
        }
      })

      setAnnotations(updatedAnnotations)
      debouncedSave(updatedAnnotations)
    },
    [setAnnotations, debouncedSave]
  )

  const flushPendingSave = useCallback(() => {
    debouncedSave.flush()
  }, [debouncedSave])

  return {
    updateLabel,
    updateSeverity,
    updateNotes,
    updateDamageTypes,
    flushPendingSave,
  }
}

/**
 * @deprecated Use useAnnotationAutoSave instead
 */
export const useNotesAutoSave = useAnnotationAutoSave
